
import React from "react";
import { Schemes } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import { Elements } from "../../../../shared/Elements";
import * as ReactRouterDom from "react-router-dom";
import { BaseService, IMember, ITeam, ITeamMember, SanitizedResponse, TeamReceivingGroup, TeamRole, TeamService } from "../../../../../services";
import { BoolHelper, Button, ButtonManageRef, Checkbox, Dropdown, Form, FormControl, Scheme, TextInput } from "@rivtn/noseur";
import { Siren } from "../../../../shared/ConfirmDialog";
import { usePlan } from "@syllektis/oya-gbewa-react";

export interface TeamEarningsProps {
    team: ITeam;
    profile: IMember;
}

function TeamEarnings(props: TeamEarningsProps) {

    const { team } = props;
    const teamService = TeamService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const [isTeamOwner, setIsTeamOwner] = React.useState<boolean>(false);
    const labels = i18nManager.Labels.teams.settings.general.team_earnings;
    const { configureTeamEarners } = usePlan({ configureTeamEarners: false });
    const [teamModerators, setTeamModerators] = React.useState<ITeamMember[]>();
    const receivingGroupOptions = [
        {
            value: TeamReceivingGroup.OWNER,
            label: labels.receivers.owner_you,
        },
        {
            label: labels.receivers.select_a_moderator,
            value: TeamReceivingGroup.SPECIFIC_MODERATOR,
        },
        {
            label: labels.receivers.selected_members,
            value: TeamReceivingGroup.SELECTED_TEAM_MEMBERS,
        },
        {
            label: labels.receivers.split_equally_bet_mods,
            value: TeamReceivingGroup.SPLIT_EQUALLY_BETWEEN_MODERATORS,
        },
        {
            label: labels.receivers.split_equally_bet_mems,
            value: TeamReceivingGroup.SPLIT_EQUALLY_BETWEEN_TEAM_MEMBERS,
        },
    ];

    React.useEffect(fetchInformation, []);

    return (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
        <span>{labels.info.part_1}</span>
        <ul>
            <li>{labels.info.part_2}</li>
            <li>{labels.info.part_3}</li>
            <li>{labels.info.part_4}</li>
        </ul>
        <span>{labels.info.part_5}</span>
        <Form scheme={Schemes.OMNEHOST} style={{ marginTop: 10 }}>
            <FormControl isFieldset fill scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={labels.who_receives}>
                <Dropdown fill disabled={!configureTeamEarners || !isTeamOwner} selectedOptionIndex={receivingGroupOptions.findIndex((o) => o.value === team.earning_settings.receiving_group)} options={receivingGroupOptions}
                    popoverFooterTemplate={() => (<div style={{ display: "flex", flexDirection: "column", borderTop: "1px solid var(--underlineColor, #E3E3E3)", paddingTop: 10, paddingBottom: 10 }}>
                        {teamModerators?.map((teamManager) => {
                            return (<div key={teamManager.external_id} style={{ display: "flex", margin: 15, marginTop: 10 }}>
                                <Checkbox scheme={Schemes.OMNEHOST} disabled={!BoolHelper.equalsAny(team.earning_settings.receiving_group, [TeamReceivingGroup.SPECIFIC_MODERATOR, TeamReceivingGroup.SELECTED_TEAM_MEMBERS])}
                                    defaultChecked={team.earning_settings.receiving_member_ids?.includes(teamManager?.external_id ?? "")} onChecked={(v) => {
                                        const entryIndex = team.earning_settings.receiving_member_ids.indexOf(teamManager.external_id);
                                        if (entryIndex < 0) {
                                            team.earning_settings.receiving_member_ids.push(teamManager.external_id);
                                        } else {
                                            team.earning_settings.receiving_member_ids.splice(entryIndex, 1);
                                        }
                                    }} />
                                <div style={{ display: "flex", marginLeft: 10 }}>
                                    <img alt={teamManager.member?.username} src={teamManager.member?.profile_picture_url} style={{ width: 40, height: 40, borderRadius: "100%" }} />
                                    <div style={{ display: "flex", flexDirection: "column", marginLeft: 10, alignItems: "center", justifyContent: "center" }}>
                                        <span style={{ fontWeight: "bold" }}>{teamManager.member?.username}</span>
                                        <span>{teamManager.member?.user_detail?.email}</span>
                                    </div>
                                </div>
                            </div>);
                        })}
                    </div>)} onSelectOption={(o) => {
                        team.earning_settings.receiving_group = o.value;
                        if (o.value === TeamReceivingGroup.OWNER) {
                            team.earning_settings.receiving_member_ids = [teamModerators?.find((h) => h.role === TeamRole.OWNER)!.external_id!];
                        } else if (o.value === TeamReceivingGroup.SPLIT_EQUALLY_BETWEEN_TEAM_MEMBERS) {
                            team.earning_settings.receiving_member_ids = teamModerators?.map((h) => h.external_id ?? "") ?? [];
                        } else if (o.value === TeamReceivingGroup.SPLIT_EQUALLY_BETWEEN_MODERATORS) {
                            team.earning_settings.receiving_member_ids = teamModerators?.filter((t) => t.role !== TeamRole.MEMBER).map((h) => h.external_id ?? "") ?? [];
                        }
                    }} />
            </FormControl>
            <p style={{ margin: "20px 0px" }}>{labels.address_info}</p>
            <FormControl isFieldset fill label={labels.address}>
                <TextInput defaultValue={team.earning_settings.address?.address_line1} onInputComplete={(v) => {
                    if (!team.earning_settings.address) team.earning_settings.address = {} as any;
                    team.earning_settings.address!.address_line1 = v;
                }} />
            </FormControl>
            <Dropdown noIcon fill scheme={Schemes.OMNEHOST} formControlProps={{ className: "plan-dd", isFieldset: true, label: labels.country, style: { marginTop: 10 } }}
                selectedOptionIndex={(i18nManager.CountriesAndCurrencies?.findIndex((p) => p?.value === (team.earning_settings.address?.country ?? team.country_code ?? "NGA")) ?? 0)}
                textInputProps={{ style: { paddingLeft: 15 } }} options={i18nManager.CountriesAndCurrencies} onSelectOption={(o) => {
                    if (!team.earning_settings.address) team.earning_settings.address = {} as any;
                    team.earning_settings.address!.country = o.value;
                }} />
            <div style={{ display: "flex", marginTop: 20 }}>
                <FormControl isFieldset fill label={labels.city} style={{ flex: 1, marginRight: 10 }}>
                    <TextInput defaultValue={team.earning_settings.address?.city} onInputComplete={(v) => {
                        if (!team.earning_settings.address) team.earning_settings.address = {} as any;
                        team.earning_settings.address!.city = v;
                    }} />
                </FormControl>
                <FormControl isFieldset fill label={labels.state} style={{ flex: 1, marginLeft: 10 }}>
                    <TextInput defaultValue={team.earning_settings.address?.state} onInputComplete={(v) => {
                        if (!team.earning_settings.address) team.earning_settings.address = {} as any;
                        team.earning_settings.address!.state = v;
                    }} />
                </FormControl>
            </div>
            <FormControl isFieldset fill label={labels.delivery_contact} style={{ marginTop: 15 }}>
                <TextInput defaultValue={team.earning_settings.address?.delivery_contact_phone} onInputComplete={(v) => {
                    if (!team.earning_settings.address) team.earning_settings.address = {} as any;
                    team.earning_settings.address!.delivery_contact_phone = v;
                }} />
            </FormControl>
            <Checkbox style={{ marginTop: 20 }} defaultChecked={team.earning_settings.accepted_terms} onChecked={(v) => { team.earning_settings.accepted_terms = v; }}
                label={<span style={{ color: "var(--textColor)" }}>
                    {labels.accept_policy.accept}{" "}
                    <a target="_blank" href={labels.accept_policy.omne_policy.location}>{labels.accept_policy.omne_policy.label}</a></span>} />
        </Form>
        <Button scheme={Schemes.OMNEHOST} loadingProps={Elements.BUTTON_LOADING_PROPS} text={i18nManager.Labels.common.save_changes}
            disabled={!isTeamOwner} style={{ marginTop: 30, padding: "10px 25px", alignSelf: "flex-end" }} onClick={(e) => {
                updateTeamEarning(team.earning_settings, e.manageRef);
            }} />
    </div>);

    function fetchInformation() {
        teamService.queryTeamMembers(team.external_id, { status: "ACTIVE", role__in: "OWNER,MODERATOR" }).then(({ content }: SanitizedResponse<ITeamMember>) => {
            setTeamModerators(content);
            for (const member of content) {
                if (member.role === TeamRole.OWNER && member.member?.external_id === profile.external_id) {
                    setIsTeamOwner(true);
                    break;
                }
            }
        }).catch(BaseService.reportError);
    }

    function updateTeamEarning(payload: any, manageRef?: ButtonManageRef) {
        manageRef?.setLoadingState(true);
        teamService.updateTeamEarnings(team.external_id, payload).then(({ sanitized }: SanitizedResponse<any>) => {
            Siren.alert(labels.update_success);
        }).catch(BaseService.reportError).finally(() => {
            manageRef?.setLoadingState(false);
        });
    }

}

export default TeamEarnings;
